/**
 * Announcements - spec 18 N-1. An admin writes a message, and every active
 * member gets it in their inbox and as a push notification.
 *
 * There is no draft or scheduling. Send is final, so the confirm step names the
 * reach from `GET /admin/announcements/audience`, fetched when the admin presses
 * Send rather than on page load. That way the number reflects whoever is active
 * at that moment.
 */

import { useState } from "react";

import { PrimaryCell } from "../components/DataTable";
import {
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorNotice,
  Field,
  Input,
  LoadingRows,
  Page,
  Pagination,
  SuccessNotice,
  Textarea,
} from "../components/primitives";
import {
  BODY_MAX,
  TITLE_MAX,
  fetchAnnouncementAudience,
  fetchAnnouncements,
  sendAnnouncement,
  type AnnouncementPage,
} from "../lib/api/announcements";
import { formatCount, formatDateTime, orDash } from "../lib/format";
import { confirmAction, useMutation, useQuery } from "../lib/hooks";

const LIMIT = 20;

export function AnnouncementsScreen() {
  const [offset, setOffset] = useState(0);
  const history = useQuery<AnnouncementPage>(
    () => fetchAnnouncements({ limit: LIMIT, offset }),
    [offset],
  );
  const [form, setForm] = useState({ title: "", body: "" });
  const [sent, setSent] = useState<string | null>(null);

  const title = form.title.trim();
  const body = form.body.trim();

  const send = useMutation(
    async () => {
      const { recipient_count } = await fetchAnnouncementAudience();
      if (recipient_count === 0) {
        throw new Error("There are no active members to send this to.");
      }
      const noun = recipient_count === 1 ? "member" : "members";
      if (!confirmAction(`Send "${title}" to ${formatCount(recipient_count)} active ${noun}? This cannot be undone.`)) {
        return null;
      }
      return sendAnnouncement({ title, body });
    },
    {
      onSuccess: (result) => {
        if (!result) return;
        const n = result.announcement.recipient_count;
        setSent(`${result.message} It went to ${formatCount(n)} ${n === 1 ? "member" : "members"}.`);
        setForm({ title: "", body: "" });
        setOffset(0);
        history.reload();
      },
    },
  );

  return (
    <Page>
      {sent ? <SuccessNotice message={sent} onDismiss={() => setSent(null)} /> : null}

      <Card>
        <CardHeader
          title="New announcement"
          hint="Goes to every active member's inbox, and to their phone if they allowed notifications."
        />
        {send.error ? <ErrorNotice error={send.error} /> : null}
        <div className="stack" style={{ gap: 16 }}>
          <Field label="Title (required)" hint={`${form.title.length}/${TITLE_MAX}`}>
            {(id) => (
              <Input
                id={id}
                value={form.title}
                maxLength={TITLE_MAX}
                placeholder="Gym closed for Dashain"
                onChange={(event) => setForm({ ...form, title: event.target.value })}
              />
            )}
          </Field>
          <Field label="Message (required)" hint={`${form.body.length}/${BODY_MAX}`}>
            {(id) => (
              <Textarea
                id={id}
                rows={5}
                value={form.body}
                maxLength={BODY_MAX}
                placeholder="Fitzone is closed from the 10th to the 14th. Happy Dashain!"
                onChange={(event) => setForm({ ...form, body: event.target.value })}
              />
            )}
          </Field>
          <div className="row row--tight">
            <Button
              variant="accent"
              disabled={!title || !body || send.pending}
              onClick={() => void send.run()}
            >
              {send.pending ? "Sending…" : "Send to all active members"}
            </Button>
          </div>
        </div>
      </Card>

      <Card flush>
        <div style={{ padding: "24px 24px 16px" }}>
          <CardHeader
            title="Sent"
            actions={
              <Button size="sm" onClick={history.reload} disabled={history.refreshing}>
                {history.refreshing ? "Refreshing…" : "Refresh"}
              </Button>
            }
          />
        </div>

        {history.error ? <ErrorNotice error={history.error} onRetry={history.reload} /> : null}
        {history.loading && !history.data ? <LoadingRows rows={4} columns={4} /> : null}

        {history.data && history.data.items.length === 0 ? (
          <EmptyState
            title="No announcements yet."
            body="Anything you send appears here, with how many members it reached."
          />
        ) : null}

        {history.data && history.data.items.length > 0 ? (
          <>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">Announcement</th>
                    <th scope="col">Sent by</th>
                    <th scope="col" style={{ textAlign: "right" }}>
                      Members reached
                    </th>
                    <th scope="col">Sent</th>
                  </tr>
                </thead>
                <tbody>
                  {history.data.items.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <PrimaryCell title={item.title} detail={item.body} />
                      </td>
                      <td>{orDash(item.created_by_name)}</td>
                      <td className="table__num">{formatCount(item.recipient_count)}</td>
                      <td>{formatDateTime(item.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              total={history.data.total}
              limit={history.data.limit}
              offset={history.data.offset}
              onChange={setOffset}
              noun="announcements"
            />
          </>
        ) : null}
      </Card>
    </Page>
  );
}
